import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import type { PricingPlans } from '../../lib/types'

const TIER_SIZE: Record<string, string> = {
  solo_provider: '1 provider',
  group_practice: '2–10 providers',
  clinic: '11–50 providers',
  enterprise: '50+ providers or multi-location organizations',
}

const FEATURES: Record<string, string[]> = {
  solo_provider: ['Provider profile', 'Routing', 'Session 4 tracking', 'Basic continuity dashboard'],
  group_practice: ['Everything in Solo', 'Shared practice dashboard', 'Multi-provider management', 'Team visibility', 'Practice continuity insights'],
  clinic: ['Everything in Group', 'Capacity management', 'Advanced reporting', 'Billing controls', 'Operational analytics', 'Provider benchmarking'],
  enterprise: ['Everything in Clinic', 'Multi-location support', 'Custom workflows', 'Dedicated onboarding', 'API access', 'Priority support', 'Contracted pricing'],
}

// "Who it's for / operational benefit / expected workflow" — one level of
// enterprise-buyer context per tier, beneath the fee/feature card itself.
const TIER_EXPLAINER: Record<string, { who: string; benefit: string; workflow: string }> = {
  solo_provider: {
    who: 'An individual clinician practicing on their own.',
    benefit: 'Routing and continuity visibility without any upfront cost.',
    workflow: 'Create a profile, complete onboarding, and start receiving routed patients.',
  },
  group_practice: {
    who: 'A group practice or small clinic with multiple providers under one roof.',
    benefit: 'Shared visibility into caseload, continuity, and team capacity across the whole practice.',
    workflow: 'A practice owner or admin manages providers; each provider sees their own routed caseload.',
  },
  clinic: {
    who: 'A larger clinic or multi-provider organization with operational and billing complexity.',
    benefit: 'Operational analytics and provider benchmarking across a bigger, busier caseload.',
    workflow: 'Practice administrators get capacity management and reporting on top of everything in Group.',
  },
  enterprise: {
    who: 'Health plans, provider networks, and multi-location behavioral health organizations.',
    benefit: 'A routing and continuity layer that extends across every location and every contracted provider.',
    workflow: 'Implementation is scoped to your existing network and workflows — contact us to design it together.',
  },
}

// The note per card explains whichever fee is actually meaningful for that
// tier — Solo Provider's seat fee is already $0, so its note explains the
// outcome fee instead. Every paid tier (non-zero or custom seat fee) gets
// the seat-activation timing note instead.
const SEAT_ACTIVATION_NOTE = 'Seat fees activate only after a matched patient attends their first session.'
const ACTIVATION_NOTES: Record<string, string> = {
  solo_provider: 'Charged only when a matched patient reaches Session 4.',
  group_practice: SEAT_ACTIVATION_NOTE,
  clinic: SEAT_ACTIVATION_NOTE,
  enterprise: SEAT_ACTIVATION_NOTE,
}

const CTA_LABEL: Record<string, string> = {
  solo_provider: 'Get Started',
  group_practice: 'Get Started',
  clinic: 'Request Demo',
  enterprise: 'Talk to Sales',
}
const CTA_LINK: Record<string, string> = {
  solo_provider: '/signup/practice',
  group_practice: '/signup/practice',
  clinic: '/contact',
  enterprise: '/contact',
}

// Exact current pricing, used only if the live /pricing-plans fetch fails —
// the page must never render with no numbers on it. Keep in sync with
// backend/app/core/pricing.py (PRICING_TIERS) if those values ever change.
const FALLBACK_PLANS: PricingPlans = {
  solo_provider: { label: 'Solo Provider', seat_fee_cents: 0, outcome_fee_cents: 3900 },
  group_practice: { label: 'Group Practice', seat_fee_cents: 5900, outcome_fee_cents: 3900 },
  clinic: { label: 'Clinic', seat_fee_cents: 9900, outcome_fee_cents: 3900 },
  enterprise: { label: 'Enterprise', seat_fee_cents: null, outcome_fee_cents: null, contact_sales: true },
}

const TIER_ORDER = ['solo_provider', 'group_practice', 'clinic', 'enterprise']
const TIER_COLUMN_LABEL: Record<string, string> = {
  solo_provider: 'Solo',
  group_practice: 'Group',
  clinic: 'Clinic',
  enterprise: 'Enterprise',
}

type ComparisonRow =
  | { type: 'feature'; label: string; from: string }
  | { type: 'value'; label: string; values: Record<string, string> }

const COMPARISON_ROWS: ComparisonRow[] = [
  { type: 'value', label: 'Outcome Fee', values: { solo_provider: '$39', group_practice: '$39', clinic: '$39', enterprise: 'Custom' } },
  { type: 'feature', label: 'Seat Activates After First Matched Session', from: 'solo_provider' },
  { type: 'feature', label: 'Provider profile', from: 'solo_provider' },
  { type: 'feature', label: 'Routing', from: 'solo_provider' },
  { type: 'feature', label: 'Session 4 tracking', from: 'solo_provider' },
  { type: 'feature', label: 'Basic continuity dashboard', from: 'solo_provider' },
  { type: 'feature', label: 'Shared practice dashboard', from: 'group_practice' },
  { type: 'feature', label: 'Multi-provider management', from: 'group_practice' },
  { type: 'feature', label: 'Team visibility', from: 'group_practice' },
  { type: 'feature', label: 'Practice continuity insights', from: 'group_practice' },
  { type: 'feature', label: 'Capacity management', from: 'clinic' },
  { type: 'feature', label: 'Advanced reporting', from: 'clinic' },
  { type: 'feature', label: 'Billing controls', from: 'clinic' },
  { type: 'feature', label: 'Operational analytics', from: 'clinic' },
  { type: 'feature', label: 'Provider benchmarking', from: 'clinic' },
  { type: 'feature', label: 'Multi-location support', from: 'enterprise' },
  { type: 'feature', label: 'Custom workflows', from: 'enterprise' },
  { type: 'feature', label: 'Dedicated onboarding', from: 'enterprise' },
  { type: 'feature', label: 'API access', from: 'enterprise' },
  { type: 'feature', label: 'Priority support', from: 'enterprise' },
]

const FAQ: { q: string; a: string }[] = [
  { q: 'When am I charged?', a: 'Provider seats activate only after a matched patient attends their first session.' },
  { q: 'What happens at signup?', a: 'No charges occur at signup. You can create your account and complete setup at no cost.' },
  { q: 'What if I add providers?', a: 'Adding a provider does not trigger a charge. A provider seat activates only after a matched patient attends their first session with that provider.' },
  { q: 'When is the outcome fee charged?', a: 'Only when a matched patient reaches Session 4.' },
  { q: 'Why Session 4?', a: "ATUUNE uses Session 4 — a patient's fourth session with their routed provider — as an operational continuity milestone, not as proof of clinical outcome. See the Platform page for the research context behind early-session engagement." },
  { q: 'What if a patient stops before Session 4?', a: 'No outcome fee is charged.' },
]

function formatCents(cents: number): string {
  return cents === 0 ? '$0' : `$${(cents / 100).toFixed(0)}`
}

export default function Pricing() {
  // Seeded with the real fallback numbers (not an empty/loading state) so
  // there's never a moment — including in prerendered/pre-hydration HTML —
  // where this page shows no pricing. The live fetch below still runs and
  // silently replaces these with server data when it succeeds.
  const [plans, setPlans] = useState<PricingPlans>(FALLBACK_PLANS)
  const [loading, setLoading] = useState(false)
  const [usingFallback, setUsingFallback] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const data = await api('/pricing-plans')
        if (cancelled) return
        setPlans(data)
        setUsingFallback(false)
      } catch {
        // Never let pricing render as an empty section — fall back to the
        // last-known-good static numbers instead of showing nothing.
        if (cancelled) return
        setPlans(FALLBACK_PLANS)
        setUsingFallback(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  return (
    <section className="section light" style={{ minHeight: '80vh' }}>
      <h2>Pricing That Aligns With Early Continuity</h2>
      <p className="lead muted">
        Outcome-based pricing is tied to a measurable operational milestone: a matched patient attending
        Session 4. No charges occur at signup. Provider seat fees activate only after a matched patient
        attends their first session.
      </p>
      <p className="muted" style={{ fontSize: 13.5, marginTop: 4 }}>Choose the tier that best matches the size of your organization.</p>

      {usingFallback && (
        <p className="form-error" style={{ marginTop: 8 }}>
          We couldn't reach our live pricing service just now, so we're showing standard pricing current as
          of today. <a href="" onClick={(e) => { e.preventDefault(); window.location.reload() }}>Refresh</a> to try again.
        </p>
      )}

      {loading ? (
        <div className="page-loading">Loading pricing…</div>
      ) : (
        <div className="pricing-grid">
          {Object.entries(plans).map(([key, tier]) => (
            <div key={key} className={`pricing-card ${key === 'group_practice' ? 'featured' : ''}`}>
              {key === 'group_practice' && <span className="featured-tag">Most popular</span>}
              <h3 className="plan-name">{tier.label}</h3>
              <span className="plan-size">{TIER_SIZE[key]}</span>
              <p className="muted" style={{ fontSize: 13, marginTop: -6, marginBottom: 10 }}>{TIER_EXPLAINER[key]?.who}</p>

              {tier.contact_sales ? (
                <div className="contact-sales-amount">Custom Pricing</div>
              ) : (
                <div className="fee-block">
                  <div className="fee-row">
                    <span className="fee-label">Seat fee</span>
                    <span className="fee-amount-line">
                      <span className="fee-amount">{formatCents(tier.seat_fee_cents!)}</span>
                      {tier.seat_fee_cents! > 0 && <span className="fee-unit">/provider/month</span>}
                    </span>
                  </div>
                  <div className="fee-row">
                    <span className="fee-label">Outcome fee</span>
                    <span className="fee-amount-line">
                      <span className="fee-amount">{formatCents(tier.outcome_fee_cents!)}</span>
                    </span>
                  </div>
                </div>
              )}
              <p className="activation-note">{ACTIVATION_NOTES[key]}</p>

              <ul>
                {(FEATURES[key] || []).map((f) => <li key={f}>{f}</li>)}
              </ul>

              <Link className="btn full" to={CTA_LINK[key]} style={{ display: 'block', textAlign: 'center' }}>
                {CTA_LABEL[key]}
              </Link>
            </div>
          ))}
        </div>
      )}

      <div className="section-block">
        <h3>Only pay when continuity is established.</h3>
        <p className="lead muted">
          Provider seats are activated only after a matched patient attends their first session. Outcome
          fees are charged only when a matched patient reaches Session 4 — an operational continuity
          milestone, not a claim about clinical outcome. No charges occur at signup. No charges occur when
          a provider is added.
        </p>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>What Each Tier Is Built For</h2></div>
        <div className="cards">
          {TIER_ORDER.map((key) => (
            <div key={key} className="card">
              <h3>{plans[key]?.label}</h3>
              <p style={{ marginBottom: 8 }}>{TIER_EXPLAINER[key].benefit}</p>
              <p className="muted" style={{ fontSize: 13 }}>{TIER_EXPLAINER[key].workflow}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Illustrative ROI Example</h2></div>
        <p className="muted" style={{ fontSize: 13 }}>
          For illustration purposes only — a hypothetical 20-provider organization, not a real customer
          result.
        </p>
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="roi-table">
            <thead>
              <tr><th>Illustrative metric</th><th>Before</th><th>With ATUUNE</th></tr>
            </thead>
            <tbody>
              <tr><td>Providers</td><td>20</td><td>20</td></tr>
              <tr><td>Early dropout rate (before Session 4)</td><td>~45%</td><td>~35%</td></tr>
              <tr><td>Additional patients reaching Session 4 / month</td><td>—</td><td>~8</td></tr>
              <tr><td>Illustrative recovered revenue / month*</td><td>—</td><td>~$3,100</td></tr>
              <tr><td>Capacity utilization</td><td>Baseline</td><td>Improved by reducing early-dropout churn</td></tr>
            </tbody>
          </table>
        </div>
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          *Illustrative only, based on a hypothetical average per-session rate — not a guarantee or
          projection for any specific organization. Actual results depend on caseload, network
          composition, and existing continuity practices.
        </p>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Compare plans</h2></div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Feature</th>
                {TIER_ORDER.map((t) => <th key={t}>{TIER_COLUMN_LABEL[t]}</th>)}
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  {row.type === 'value'
                    ? TIER_ORDER.map((t) => <td key={t} style={{ textAlign: 'center' }}>{row.values[t]}</td>)
                    : TIER_ORDER.map((t, i) => {
                        const fromIndex = TIER_ORDER.indexOf(row.from)
                        return (
                          <td key={t} style={{ textAlign: 'center' }}>
                            {i >= fromIndex ? <span style={{ color: 'var(--warm-terracotta)', fontWeight: 700 }}>✓</span> : <span className="muted">—</span>}
                          </td>
                        )
                      })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Frequently asked questions</h2></div>
        <div className="row-list">
          {FAQ.map((item) => (
            <div key={item.q} style={{ padding: '14px 4px', borderBottom: '1px solid var(--warm-line)' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: 15.5 }}>{item.q}</h3>
              <p className="muted" style={{ margin: 0, fontSize: 14 }}>{item.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
